/**
 * Market Opportunities service (Fundamental Analysis Module — Phase 4).
 *
 * Talks only to this app's own FastAPI backend (`/api/v1/market-opportunities/*`)
 * — read-only, same convention as `stockMarketService.listScanner`/
 * `getStockHistory`: this page never calculates anything itself, it only
 * reads what the Indicator Engine / canonical Breakout Score Engine
 * already computed.
 */

import api from "@/services/api";
import type {
  BreakoutDetailResponse,
  BreakoutListResponse,
  MarketOpportunityListResponse,
  MarketOpportunitySegment,
  MarketOpportunitySegmentResponse,
  MarketOpportunityTab,
  NseUniverseImportJobStatusResponse,
} from "@/types";

export interface NseUniverseImportTriggerBody {
  from_date: string;
  to_date: string;
  interval?: string;
}

export interface MarketOpportunitySearchParams {
  search?: string;
  page?: number;
  page_size?: number;
  sort_by?: "breakout_score" | "close_price" | "market_cap_cr" | "status_since_date";
  sort_order?: "asc" | "desc";
}

const TAB_PATHS: Record<MarketOpportunityTab, string> = {
  top_breakout: "/market-opportunities/top-breakout",
  shift_to_breakout: "/market-opportunities/shift-to-breakout",
};

// Phase 5 (Market Cap Segments) + Stock Scoring Standardization's
// `nifty-50` addition — a SEPARATE set of endpoints from the tabs above
// (fixed top-10, no search/sort/pagination), see backend
// `app.api.v1.market_opportunities` router docstring.
const SEGMENT_PATHS: Record<MarketOpportunitySegment, string> = {
  top: "/market-opportunities/top",
  "nifty-50": "/market-opportunities/nifty-50",
  "large-cap": "/market-opportunities/large-cap",
  "mid-cap": "/market-opportunities/mid-cap",
  "small-cap": "/market-opportunities/small-cap",
  "micro-cap": "/market-opportunities/micro-cap",
};

export const marketOpportunityService = {
  /** `tab` omitted (or `undefined`) returns all statuses — used for the "All" view, if any. */
  async list(
    tab: MarketOpportunityTab | undefined,
    params: MarketOpportunitySearchParams = {}
  ): Promise<MarketOpportunityListResponse> {
    const path = tab ? TAB_PATHS[tab] : "/market-opportunities";
    const { data } = await api.get<MarketOpportunityListResponse>(path, { params });
    return data;
  },

  /** Phase 5 + NIFTY 50: one of the Market Cap Segment / NIFTY 50 views —
   * no params, the backend always returns up to 10 rows, pre-sorted by
   * canonical Breakout Score descending. */
  async getSegment(segment: MarketOpportunitySegment): Promise<MarketOpportunitySegmentResponse> {
    const { data } = await api.get<MarketOpportunitySegmentResponse>(SEGMENT_PATHS[segment]);
    return data;
  },

  /** Breakout Score Engine V2 (Breakouts tab) — reads pre-calculated,
   * persisted results only (no dynamic computation on this path); no
   * params, always returns every scored stock, sorted highest Breakout
   * Score first. */
  async getBreakouts(): Promise<BreakoutListResponse> {
    const { data } = await api.get<BreakoutListResponse>("/market-opportunities/breakouts");
    return data;
  },

  /** Breakout Score Engine V2 — the full per-indicator breakdown for ONE
   * stock, fetched only when a user expands that row (see
   * `MarketOpportunitiesPage`'s `loadBreakoutDetail`). */
  async getBreakoutDetail(symbol: string): Promise<BreakoutDetailResponse> {
    const { data } = await api.get<BreakoutDetailResponse>(
      `/market-opportunities/breakouts/${encodeURIComponent(symbol)}`
    );
    return data;
  },

  // ---- Phase 5: NSE-universe bulk import/score admin action ----
  // Admin-only (backend enforces `require_roles("ADMIN")`). A SEPARATE,
  // broader action from `stockMarketService.recomputeAllActive()` /
  // `importAll()` — those only touch the original NIFTY 50 `is_active`
  // stocks; this one covers the entire NSE universe seeded by the bulk
  // security-master script, and runs as a background job (see
  // `startNseUniverseImport`'s immediate response vs. `getNseUniverseImportStatus`
  // for polling).

  async startNseUniverseImport(
    body: NseUniverseImportTriggerBody
  ): Promise<NseUniverseImportJobStatusResponse> {
    const { data } = await api.post<NseUniverseImportJobStatusResponse>(
      "/market-opportunities/admin/import-nse-universe",
      body
    );
    return data;
  },

  async getNseUniverseImportStatus(): Promise<NseUniverseImportJobStatusResponse | null> {
    const { data } = await api.get<NseUniverseImportJobStatusResponse | null>(
      "/market-opportunities/admin/import-nse-universe/status"
    );
    return data;
  },
};

export default marketOpportunityService;
